import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  MotifFormField,
  MotifSelect,
  MotifOption,
  MotifDatePicker,
  MotifAccordion,
  MotifAccordionTrigger,
  MotifAccordionContent,
} from "@ey-xd/motif-react";
import { routes } from "../../../routes/routes.constatns";
import { ArrowLeftIcon } from "../../../assets/Icons/ArrowLeft";
import "./CreateNewRequest.scss";
import { CloseIcon } from "../../../assets/Icons/CloseIcon";
import { CalendarIcon } from "../../../assets/Icons/Calendar";
import { Table2ColumnsIcon } from "../../../assets/Icons/Table2ColumnsIcon";
import { IconoirArrowDown } from "@ey-xd/motif-icon";
import { NavDownArrow } from "../../../assets/Icons/NavArrowDown";
import NavArrowUp from "../../../assets/Icons/NavArrowUp";
import { ArrowRightIcon } from "../../../assets/Icons/ArrowRightIcon";
import { LookupService, QualityFrameworkService } from "../../../api/services";
import { hasQualitySpocRole } from "../qualityFramework.utils";
import { QualityFrameworkAccessDenied } from "../QualityFrameworkAccessDenied";
import { Tooltip } from "../../CommonUIComponents/Tooltip/Tooltip";

// "Trade Receivables Analyzer" -> "TRA"
const abbreviateAnalyzer = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 3)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

// First-column label is only truncated once the analyzer columns crowd out its space
const LABEL_TRUNCATE_LENGTH = 20;
const MANY_COLUMNS_THRESHOLD = 4;

const renderSummaryLabelCell = (label, columnCount) => {
  const text = String(label ?? "");
  const shouldTruncate =
    columnCount > MANY_COLUMNS_THRESHOLD && text.length > LABEL_TRUNCATE_LENGTH;

  if (!shouldTruncate) {
    return text;
  }

  return (
    <Tooltip
      trigger={
        <span className="cnr-truncated-label">
          {`${text.slice(0, LABEL_TRUNCATE_LENGTH)}...`}
        </span>
      }
      tooltipText={text}
      placement="top"
    />
  );
};

const removeFsoSuffix = (name = "") =>
  String(name).trim().replace(/\s+FSO$/i, "");

const renderNoData = () => (
  <div className="cnr-population-empty">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6V18C4 18 4 21 12 21C20 21 20 18 20 18V6" />
      <path d="M4 12C4 12 4 15 12 15C20 15 20 12 20 12" />
      <path d="M12 3C20 3 20 6 20 6C20 6 20 9 12 9C4 9 4 6 4 6C4 6 4 3 12 3Z" />
    </svg>
    <span>No data</span>
  </div>
);

export const CreateNewRequest = () => {
  const navigate = useNavigate();
  const userRoles = useSelector((state) => state.user?.userRoles);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);
  const hasAccess = hasQualitySpocRole(userRoles);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [area, setArea] = useState("");
  const [superRegions, setSuperRegions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [superRegionOptions, setSuperRegionOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [isLoadingSuperRegions, setIsLoadingSuperRegions] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [numberOfSamples, setNumberOfSamples] = useState("");
  const [populationResponse, setPopulationResponse] = useState(null);
  const [isLoadingPopulation, setIsLoadingPopulation] = useState(false);
  const [isIdentifyingPopulation, setIsIdentifyingPopulation] = useState(false);
  const [populationCountDetails, setPopulationCountDetails] = useState(null);
  const [hasPopulationDetailsResponse, setHasPopulationDetailsResponse] =
    useState(false);
  const [isLoadingPopulationDetails, setIsLoadingPopulationDetails] =
    useState(false);
  const populationRequestId = useRef(0);
  const [isRegionOpen, setIsRegionOpen] = useState(true);
  const [isReviewerOpen, setIsReviewerOpen] = useState(true);

  const populationData = populationResponse?.data ?? populationResponse;
  const populationCounts = populationData?.populationCounts ?? [];
  const hasInvalidDateRange =
    startDate instanceof Date &&
    endDate instanceof Date &&
    startDate.getTime() > endDate.getTime();

  // API returns names, selects hold ids/guids, so map the selection back to names
  const selectedSuperRegionNames = superRegionOptions
    .filter((opt) => superRegions.includes(opt.superRegionGuid))
    .map((opt) => opt.superRegionName);

  const selectedCountryNames = countryOptions
    .filter((opt) => regions.includes(String(opt.countryId)))
    .map((opt) => removeFsoSuffix(opt.countryName));

  const filteredPopulationCounts = populationCounts.filter((item) => {
    const superRegionMatch =
      !selectedSuperRegionNames.length ||
      selectedSuperRegionNames.includes(item.superRegionName);
    const countryMatch =
      !selectedCountryNames.length ||
      selectedCountryNames.includes(item.countryName);
    return superRegionMatch && countryMatch;
  });

  const hasSelection =
    selectedSuperRegionNames.length > 0 || selectedCountryNames.length > 0;

  const totalOrders = hasSelection
    ? filteredPopulationCounts.reduce(
        (sum, item) => sum + (Number(item.count) || 0),
        0,
      )
    : (populationData?.totalCount ?? 0);

  const hasPopulationCount = populationResponse != null && !isLoadingPopulation;
  const normalizedSampleCount = numberOfSamples.trim();
  const hasInvalidSampleCount =
    hasPopulationCount &&
    totalOrders > 0 &&
    Boolean(normalizedSampleCount) &&
    (!/^\d+$/.test(normalizedSampleCount) || Number(normalizedSampleCount) <= 0);
  const sampleCountExceedsTotalOrders =
    hasPopulationCount &&
    !hasInvalidSampleCount &&
    Number(normalizedSampleCount) > totalOrders;
  const hasSampleCountError =
    hasInvalidSampleCount || sampleCountExceedsTotalOrders;

  const sampleSize =
    totalOrders > 0 && !hasSampleCountError && normalizedSampleCount
      ? ((Number(normalizedSampleCount) / totalOrders) * 100).toFixed(0)
      : 0;

  useEffect(() => {
    let isActive = true;

    if (!rolesLoaded || !hasAccess) {
      return undefined;
    }

    const loadAreas = async () => {
      try {
        setIsLoadingAreas(true);
        const response = await LookupService.getRegions();
        if (isActive) {
          setAreaOptions(response?.data || response || []);
        }
      } catch (error) {
        console.error("Error fetching areas:", error);
        if (isActive) {
          setAreaOptions([]);
        }
      } finally {
        if (isActive) {
          setIsLoadingAreas(false);
        }
      }
    };

    loadAreas();

    return () => {
      isActive = false;
    };
  }, [rolesLoaded, hasAccess]);

  useEffect(() => {
    let isActive = true;

    if (!area) {
      setSuperRegionOptions([]);
      return undefined;
    }

    const loadSuperRegions = async () => {
      try {
        setIsLoadingSuperRegions(true);
        const response = await LookupService.getSuperRegionsByRegion(area);
        if (isActive) {
          setSuperRegionOptions(response?.data || response || []);
        }
      } catch (error) {
        console.error("Error fetching super regions:", error);
        if (isActive) {
          setSuperRegionOptions([]);
        }
      } finally {
        if (isActive) {
          setIsLoadingSuperRegions(false);
        }
      }
    };

    loadSuperRegions();

    return () => {
      isActive = false;
    };
  }, [area]);

  // One request per selected super region, results merged into a single country list
  useEffect(() => {
    let isActive = true;

    if (!superRegions.length) {
      setCountryOptions([]);
      return undefined;
    }

    const loadCountries = async () => {
      try {
        setIsLoadingCountries(true);
        const responses = await Promise.all(
          superRegions.map((superRegionGuid) =>
            LookupService.getCountriesBySuperRegion(superRegionGuid),
          ),
        );

        const merged = responses.flatMap((response, index) => {
          const selectedSuperRegion = superRegionOptions.find(
            (option) => option.superRegionGuid === superRegions[index],
          );
          const isFsoSuperRegion = /(?:^|\s)FSO$/i.test(
            String(selectedSuperRegion?.superRegionName || "").trim(),
          );
          const countries = response?.data || response || [];

          return countries.map((country) => {
            if (!isFsoSuperRegion || !country?.countryName) {
              return country;
            }

            const countryName = String(country.countryName).trim();
            return /(?:^|\s)FSO$/i.test(countryName)
              ? country
              : { ...country, countryName: `${countryName} FSO` };
          });
        });
        const uniqueByCountryId = Array.from(
          new Map(
            merged.map((country) => [
              country.countryId ?? country.id ?? country.countryName,
              country,
            ]),
          ).values(),
        );

        if (isActive) {
          setCountryOptions(uniqueByCountryId);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        if (isActive) {
          setCountryOptions([]);
        }
      } finally {
        if (isActive) {
          setIsLoadingCountries(false);
        }
      }
    };

    loadCountries();

    return () => {
      isActive = false;
    };
  }, [superRegions]);

  const handleAreaChange = (value) => {
    setArea(value);
    setSuperRegions([]);
    setRegions([]);
  };

  const handleSuperRegionChange = (value) => {
    setSuperRegions(value);
    setRegions([]);
  };

  const analyzerColumns = populationCountDetails?.analyzerColumns || [];

  const regionRows = [
    ...(populationCountDetails?.populationByRegion || []).map((item) => ({
      label: item.countryName,
      analyzerCounts: item.analyzerCounts || {},
    })),
    ...(populationCountDetails?.regionTotal
      ? [
          {
            label: "Total",
            analyzerCounts:
              populationCountDetails.regionTotal.analyzerCounts || {},
          },
        ]
      : []),
  ];

  const reviewerRows = [
    ...(populationCountDetails?.populationByReviewer || []).map((item) => ({
      label: item.reviewerName,
      analyzerCounts: item.analyzerCounts || {},
    })),
    ...(populationCountDetails?.reviewerTotal
      ? [
          {
            label: "Total",
            analyzerCounts:
              populationCountDetails.reviewerTotal.analyzerCounts || {},
          },
        ]
      : []),
  ];

  const hasPositiveAnalyzerCount = (rows) =>
    rows.some((row) =>
      Object.values(row.analyzerCounts).some((count) => Number(count) > 0),
    );

  const showRegionEmptyState = !hasPositiveAnalyzerCount(regionRows);
  const showReviewerEmptyState = !hasPositiveAnalyzerCount(reviewerRows);

  // Derive ISO strings for API; picker receives Date objects directly
  const toISO = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleDateChange = async (val) => {
    const requestId = ++populationRequestId.current;

    if (val == null) {
      setStartDate(null);
      setEndDate(null);
      setPopulationResponse(null);
      return;
    }

    let nextStartDate = null;
    let nextEndDate = null;

    if (Array.isArray(val)) {
      nextStartDate = val[0] instanceof Date ? val[0] : null;
      nextEndDate = val[1] instanceof Date ? val[1] : null;
    } else if (val instanceof Date) {
      nextStartDate = val;
    }

    setStartDate(nextStartDate);
    setEndDate(nextEndDate);

    if (!nextStartDate || !nextEndDate) {
      setPopulationResponse(null);
      return;
    }

    if (nextStartDate.getTime() > nextEndDate.getTime()) {
      setPopulationResponse(null);
      return;
    }

    try {
      setIsLoadingPopulation(true);
      const response = await QualityFrameworkService.getPopulationCount(
        toISO(nextStartDate),
        toISO(nextEndDate),
      );
      if (requestId === populationRequestId.current) {
        setPopulationResponse(response);
      }
    } catch (error) {
      console.error("Error fetching population count:", error);
      if (requestId === populationRequestId.current) {
        setPopulationResponse(null);
      }
    } finally {
      if (requestId === populationRequestId.current) {
        setIsLoadingPopulation(false);
      }
    }
  };

  // Select values are guids/strings, the API expects the numeric ids from the lookup options
  const buildFilterPayload = () => {
    const selectedSuperRegionIds = superRegionOptions
      .filter((opt) => superRegions.includes(opt.superRegionGuid))
      .map((opt) => Number(opt.superRegionId ?? opt.id ?? opt.superRegionGuid))
      .filter((id) => !Number.isNaN(id));

    const selectedCountryIds = countryOptions
      .filter((opt) => regions.includes(String(opt.countryId)))
      .map((opt) => Number(opt.countryId))
      .filter((id) => !Number.isNaN(id));

    return {
      periodStartDate: startDate ? startDate.toISOString() : null,
      periodEndDate: endDate ? endDate.toISOString() : null,
      area: "EMEIA",
      superRegionIds: selectedSuperRegionIds,
      countryIds: selectedCountryIds,
      requiredSamples: Number(numberOfSamples) || 0,
    };
  };

  const handleIdentifyPopulation = async () => {
    setIsRegionOpen(true);
    setIsReviewerOpen(true);
    try {
      setIsLoadingPopulationDetails(true);
      setHasPopulationDetailsResponse(false);
      setPopulationCountDetails(null);
      const response = await QualityFrameworkService.getPopulationCountDetails(
        buildFilterPayload(),
      );
      setPopulationCountDetails(response?.data ?? response ?? null);
      setHasPopulationDetailsResponse(true);
    } catch (error) {
      console.error("Error fetching population count details:", error);
      setPopulationCountDetails(null);
    } finally {
      setIsLoadingPopulationDetails(false);
    }
  };

  const canProceedToAssignUsers =
    Boolean(startDate) &&
    Boolean(endDate) &&
    Boolean(area) &&
    superRegions.length > 0 &&
    regions.length > 0 &&
    hasPopulationCount &&
    totalOrders > 0 &&
    /^\d+$/.test(normalizedSampleCount) &&
    Number(normalizedSampleCount) > 0 &&
    !hasInvalidDateRange &&
    !hasInvalidSampleCount &&
    !sampleCountExceedsTotalOrders;

  const handleProceedToAssignUsers = async () => {
    const payload = buildFilterPayload();

    try {
      setIsIdentifyingPopulation(true);
      const response =
        await QualityFrameworkService.identifyPopulationDetails(payload);
      navigate(`/${routes.qualityFrameworkAssignUsers}`, {
        state: { populationDetails: response?.data ?? response },
      });
    } catch (error) {
      console.error("Error identifying population details:", error);
    } finally {
      setIsIdentifyingPopulation(false);
    }
  };

  if (rolesLoaded && !hasAccess) {
    return <QualityFrameworkAccessDenied onBack={() => navigate(-1)} />;
  }

  return (
    <div className="create-new-request-page">
      <div className="create-new-request-header">
        <button
          type="button"
          className="create-new-request-back-btn"
          onClick={() => navigate(`/${routes.qualityFramework}`)}
        >
          <ArrowLeftIcon width={12} height={12} color="#A4A3B1" />
          Back
        </button>
        <h1 className="create-new-request-title">Create New Request</h1>
      </div>

      <div className="create-new-request-content">
        <div className="create-new-request-card">
          <div className="cnr-filter-row">
            <div className="cnr-field">
              <label className="cnr-label">Period</label>
              <div
                className={`cnr-datepicker-field${hasInvalidDateRange ? " cnr-datepicker-field--error" : ""}`}
              >
                <MotifDatePicker
                  range
                  value={[startDate, endDate]}
                  format="dd/MM/yyyy"
                  locale="en-US"
                  calendarPlacement="top-start"
                  yearsAfter={5}
                  yearsBefore={5}
                  onChange={handleDateChange}
                  dayPlaceholder="dd"
                  monthPlaceholder="mm"
                  yearPlaceholder="yyyy"
                  clearIcon={
                    startDate || endDate ? (
                      <CloseIcon width={16} height={16} color="#A4A3B1" />
                    ) : null
                  }
                  calendarIcon={
                    <CalendarIcon width={16} height={16} color="#A4A3B1" />
                  }
                />
              </div>
              {hasInvalidDateRange && (
                <span className="cnr-validation-error" role="alert">
                  Start date should be before End Date
                </span>
              )}
            </div>

            <div className="cnr-field">
              <label className="cnr-label">Area</label>
              <MotifFormField className="cnr-select-field">
                <MotifSelect
                  value={area}
                  onChange={handleAreaChange}
                  placeholder={isLoadingAreas ? "Loading..." : "Select Area"}
                  disabled={isLoadingAreas}
                  filter
                >
                  {areaOptions.map((opt) => (
                    <MotifOption key={opt.regionGuid} value={opt.regionGuid}>
                      {opt.regionName}
                    </MotifOption>
                  ))}
                </MotifSelect>
              </MotifFormField>
            </div>

            <div className="cnr-field">
              <label className="cnr-label">Super Region</label>
              <MotifFormField className="cnr-select-field">
                <MotifSelect
                  value={superRegions}
                  onChange={handleSuperRegionChange}
                  placeholder={
                    isLoadingSuperRegions ? "Loading..." : "Select Super Region"
                  }
                  disabled={!area || isLoadingSuperRegions}
                  multiple
                  showSelectAllButton
                  filter
                >
                  {superRegionOptions.map((opt) => (
                    <MotifOption
                      key={opt.superRegionGuid}
                      value={opt.superRegionGuid}
                    >
                      {opt.superRegionName}
                    </MotifOption>
                  ))}
                </MotifSelect>
              </MotifFormField>
            </div>

            <div className="cnr-field">
              <label className="cnr-label">Region/Country</label>
              <MotifFormField className="cnr-select-field">
                <MotifSelect
                  value={regions}
                  onChange={(val) => setRegions(val)}
                  placeholder={
                    isLoadingCountries ? "Loading..." : "Select Country"
                  }
                  disabled={!superRegions.length || isLoadingCountries}
                  multiple
                  showSelectAllButton
                  filter
                >
                  {countryOptions.map((opt) => (
                    <MotifOption
                      key={opt.countryId}
                      value={String(opt.countryId)}
                    >
                      {opt.countryName}
                    </MotifOption>
                  ))}
                </MotifSelect>
              </MotifFormField>
            </div>
          </div>

          <div className="cnr-samples-row">
            <div className="cnr-samples-field">
              <label className="cnr-label" htmlFor="cnr-samples-input">
                No. of Samples required
              </label>
              <input
                id="cnr-samples-input"
                type="text"
                inputMode="decimal"
                className={`cnr-samples-input${hasSampleCountError ? " cnr-samples-input--error" : ""}`}
                placeholder="Enter no. of Samples"
                value={numberOfSamples}
                onChange={(e) => setNumberOfSamples(e.target.value)}
                disabled={!hasPopulationCount || totalOrders <= 0}
                aria-invalid={hasSampleCountError}
                aria-describedby={hasSampleCountError ? "cnr-samples-error" : undefined}
              />
              {hasSampleCountError && (
                <span id="cnr-samples-error" className="cnr-validation-error" role="alert">
                  {hasInvalidSampleCount
                    ? "Invalid value in no of samples"
                    : "Sample count exceeds total orders"}
                </span>
              )}
            </div>

            <div className="cnr-summary">
              <span className="cnr-summary-item">
                Total No. of Orders:{" "}
                <span className="cnr-summary-value">
                  {isLoadingPopulation ? "Loading..." : totalOrders}
                </span>
              </span>
              <span className="cnr-summary-item">
                Sample Size:{" "}
                <span className="cnr-summary-value">{sampleSize}%</span>
              </span>
            </div>

            <button
              type="button"
              className="cnr-identify-btn"
              onClick={handleIdentifyPopulation}
              disabled={isLoadingPopulationDetails || !canProceedToAssignUsers}
            >
              {isLoadingPopulationDetails ? "Loading..." : "Identify population"}
            </button>
          </div>
        </div>
        <div className="create-new-request-summary">
          <div className="cnr-summary-header">
            <Table2ColumnsIcon width={24} height={24} color="#A4A3B1" />
            <span className="cnr-summary-title">
              Summary of Selected Samples
            </span>
          </div>
          <div className="cnr-summary-scroll">
            <div className="cnr-summary-content">
              <MotifAccordion
                open={isRegionOpen}
                onOpen={() => setIsRegionOpen(true)}
                onClose={() => setIsRegionOpen(false)}
                alignIconRight
                useChevronIcon={true}
              >
                <MotifAccordionTrigger>
                  Population list by Region
                </MotifAccordionTrigger>
                <MotifAccordionContent>
                  {showRegionEmptyState ? (
                    renderNoData()
                  ) : (
                    <div className="cnr-table-scroll">
                    <table className="cnr-data-table">
                      <thead>
                        <tr>
                          <th />
                          {analyzerColumns.map((column) => (
                            <th key={column} title={column}>
                              {abbreviateAnalyzer(column)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {regionRows.map((row, i) => (
                          <tr key={`${row.label}-${i}`}>
                            <td>{renderSummaryLabelCell(row.label, analyzerColumns.length)}</td>
                            {analyzerColumns.map((column) => (
                              <td key={column}>{row.analyzerCounts[column] ?? 0}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </MotifAccordionContent>
              </MotifAccordion>
              <MotifAccordion
                open={isReviewerOpen}
                onOpen={() => setIsReviewerOpen(true)}
                onClose={() => setIsReviewerOpen(false)}
                alignIconRight
                useChevronIcon={true}
              >
                <MotifAccordionTrigger>
                  Population list by Reviewer
                </MotifAccordionTrigger>
                <MotifAccordionContent>
                  {showReviewerEmptyState ? (
                    renderNoData()
                  ) : (
                    <div className="cnr-table-scroll">
                      <table className="cnr-data-table">
                        <thead>
                          <tr>
                            <th />
                            {analyzerColumns.map((column) => (
                              <th key={column} title={column}>
                                {abbreviateAnalyzer(column)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reviewerRows.map((row, i) => (
                            <tr key={`${row.label}-${i}`}>
                              <td>{String(row.label ?? "")}</td>
                              {analyzerColumns.map((column) => (
                                <td key={column}>
                                  {row.analyzerCounts[column] ?? 0}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </MotifAccordionContent>
              </MotifAccordion>
            </div>
          </div>
          <div className="cnr-summary-footer">
            <button
              type="button"
              className="cnr-proceed-btn"
              onClick={handleProceedToAssignUsers}
              disabled={isIdentifyingPopulation || !canProceedToAssignUsers}
            >
              {isIdentifyingPopulation
                ? "Processing..."
                : "Proceed to Assign Users"}
              <ArrowRightIcon width={12} height={12} color="#FFFFFF" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNewRequest;
